<script lang="ts">
    import type {DocumentDetailReactivePageData, DocumentDetailStaticPageData} from '../types';
    import type {Document} from 'typings/portaro.be.types';
    import {onMount} from 'svelte';
    import {exists, isFunction} from 'shared/utils/custom-utils';
    import {fade} from 'svelte/transition';
    import {Kind} from 'shared/constants/portaro.constants';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import {KpDocumentDetailPageService} from '../kp-document-detail-page.service';
    import {getPageContext} from 'shared/layouts/page-context';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import Labels from 'shared/components/label/Labels.svelte';
    import Label from 'shared/components/label/Label.svelte';
    import KpSlider from '../../kp-slider/KpSlider.svelte';
    import KpCover from '../../kp-cover/KpCover.svelte';
    import KpSliderControls from '../../kp-slider/KpSliderControls.svelte';

    const localize = getLocalization();
    const fadeInAnimParams = {duration: 250};
    const service = getInjector().getByToken<KpDocumentDetailPageService>(KpDocumentDetailPageService.serviceName);
    const pageContext = getPageContext<DocumentDetailStaticPageData, DocumentDetailReactivePageData>();

    const model = pageContext.staticData.model;

    let loading = true;
    let similarDocuments: Document[] | undefined;
    let setIndex: ((value: number, calledFromEnd: boolean) => void) | undefined;

    onMount(async () => {
        similarDocuments = await service.getSimilarDocuments(model.record);
        loading = false;
    });

    const handleOnEnd = () => {
        if (isFunction(setIndex)) {
            setIndex(0, true);
        }
    }
</script>

{#if loading}
    <KpLoadingBlock size="sm"/>
{/if}

{#if exists(similarDocuments) && !loading && similarDocuments.length > 0}
    <div class="kp-similar-documents-container" in:fade={fadeInAnimParams}>
        <h2>{localize(/* @kp-localization record.MohloByVasZajimat */ 'record.MohloByVasZajimat')}</h2>

        <div class="documents-slider-container">
            <KpSlider gap="20px"
                      bind:setIndex
                      on:end={handleOnEnd}>

                {#each similarDocuments as document(document.id)}
                    <div class="slider-document">
                        <div class="cover-container-in-slider">
                            <KpCover record="{document}" fillTo="height"/>
                        </div>

                        <div class="title-container">
                            <Label labeled="{document}" explicitKind="{Kind.KIND_RECORD}"/>
                        </div>

                        <div class="subtitle-container">
                            {#if exists(document.subtitle) && document.subtitle.length > 0}
                                <Labels labeleds="{document.subtitle}" linkifyUrls="{false}"/>
                            {/if}
                        </div>

                        {#if exists(document.primaryAuthors) && document.primaryAuthors.length > 0}
                            <small>
                                <Labels labeleds="{document.primaryAuthors}"/>
                            </small>
                        {/if}
                    </div>
                {/each}

                <svelte:fragment slot="controls" let:currentIndex>
                    <KpSliderControls itemsCount="{similarDocuments.length}"
                                      {setIndex}
                                      {currentIndex}/>
                </svelte:fragment>
            </KpSlider>
        </div>
    </div>
{/if}

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    .kp-similar-documents-container {
        display: flex;
        flex-direction: column;
        gap: 20px;

        .documents-slider-container {
            position: relative;

            .slider-document {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                text-align: center;
                width: 220px;
                gap: 4px;
                padding: 0 @padding-base-horizontal;

                .cover-container-in-slider {
                    height: 108px;
                    margin-bottom: @padding-small-vertical;
                    transition: opacity 0.3s ease-in-out;

                    &:hover {
                        opacity: 0.75;
                    }
                }

                .title-container {
                    margin-bottom: @padding-small-vertical;
                }

                .subtitle-container {
                    color: gray;
                    font-size: 0.9em;
                }
            }
        }
    }
</style>